/**
 * CartInfoPanel - 容器组件
 * 负责切换/动画/布局，具体内容由子组件 ShippingTip 与 OrderThresholdTip 渲染。
 */
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import type { UseCartInfoResult, CartInfoTip } from '../../hooks/cart/useCartInfo';
import { ShippingTip } from './ShippingTip';
import { OrderThresholdTip } from './OrderThresholdTip';
import { t } from '@/constants/i18n';
import { CART_INFO_PANEL_MOTION } from '@/constants/animation';

interface CartInfoPanelProps {
  result: UseCartInfoResult;
  currency: string;
}

function renderTipMeta(tip: CartInfoTip, currency: string) {
  switch (tip.kind) {
    case 'SHIPPING_ESTIMATE':
      return { isWarning: false, render: <ShippingTip shippingDate={tip.shippingDate!} /> };
    case 'MIN_ORDER':
      return {
        isWarning: true,
        render: <OrderThresholdTip tip={tip} currency={currency} />
      };
    default:
      return {
        isWarning: false,
        render: (
          <>
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-xs truncate">{t('infoAvailable')}</span>
          </>
        )
      };
  }
}

export function CartInfoPanel({ result, currency }: CartInfoPanelProps) {
  const { currentTip, totalTips, prevTip, nextTip, currentIndex } = result;

  if (!currentTip) return null;

  const tipMeta = renderTipMeta(currentTip, currency);
  const showNavigation = totalTips > 1;

  return (
    <div className="mb-6 relative group w-full">
      {/* Premium Container with Glassmorphism */}
      <div
        className={`
          relative h-14 w-full min-w-0 flex items-center px-4
          bg-white/70 backdrop-blur-md rounded-2xl 
          border border-zinc-100 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]
          transition-all duration-300 
          ${tipMeta.isWarning ? 'border-orange-100 bg-orange-50/30' : ''}
        `}
      >
        {/* Navigation - Fixed Edge Overlay */}
        {showNavigation && (
          <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-1 pointer-events-none">
            <button
              onClick={prevTip}
              className="pointer-events-auto z-10 p-1.5 rounded-full text-zinc-400 md:text-zinc-300 md:hover:text-zinc-600 md:hover:bg-zinc-100/50 active:bg-transparent transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Previous info"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>


            <button
              onClick={nextTip}
              className="pointer-events-auto z-10 p-1.5 rounded-full text-zinc-400 md:text-zinc-300 md:hover:text-zinc-600 md:hover:bg-zinc-100/50 active:bg-transparent transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Next info"
            >
              <ChevronRight className="h-4 w-4" />
            </button>


          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 flex min-w-0 justify-center items-center min-h-full">
          <div className="w-full min-w-0 overflow-hidden">
            <AnimatePresence mode="wait">
              <m.div
                key={currentTip.id}
                initial={CART_INFO_PANEL_MOTION.initial}
                animate={CART_INFO_PANEL_MOTION.animate}
                exit={CART_INFO_PANEL_MOTION.exit}
                transition={CART_INFO_PANEL_MOTION.transition}
                className="w-full min-w-0 px-6 overflow-hidden"
              >
                {tipMeta.render}
              </m.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation handled by overlay */}
      </div>

      {/* Pagination Dots */}
      {showNavigation && (
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 items-center">
          {Array.from({ length: totalTips }).map((_, i) => (
            <m.div
              key={i}
              initial={false}
              animate={{
                width: i === currentIndex ? 12 : 4,
                backgroundColor: i === currentIndex ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'
              }}
              className="h-1 rounded-full"
            />
          ))}
        </div>
      )}
    </div>
  );
}
